import { useQuery } from '@apollo/client';
import {
  Button,
  Checkbox,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Text,
  Textarea,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { dinero } from 'dinero.js';
import { create } from 'lodash';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';

import {
  accountClassItem,
  fetchAccountClass,
} from '@/api/financials/accountClass';
import {
  ChartOfAccountItem,
  fetchChartOfAccounts,
} from '@/api/financials/chartOfAccounts';
import {
  FinancialAssociationEntityType,
  createFinancialAssociation,
  getFinancialAssociation,
  updateFinancialAssociation,
} from '@/api/financials/financialSettings';
import { currencyDisplay } from '@/components/Currency/Currency';
import {
  CreateOtherItemInput,
  OtherItemCategoryStatus,
  OtherItemStatus,
  OtherItemVisibility,
} from '@/graphql-types/globalTypes';
import { useCurrent } from '@/hooks/useCurrent';
import { useDefaultCurrency } from '@/hooks/useDefaultCurrency';
import CustomSelect from '@/imports/ui/chakra/components/CustomSelect';

import { GET_CATEGORY } from '../../../OtherItemsCategory/graphql';
import { useCreateOtherItem } from '../../hooks/useCreateOtherItem';
import { useUpdateOtherItem } from '../../hooks/useUpdateOtherItem';

const OTHER_ITEM_VISIBILITY_OPTIONS: {
  value: OtherItemVisibility;
  label: string;
}[] = [
  { value: OtherItemVisibility.PUBLIC, label: 'Public' },
  { value: OtherItemVisibility.MEMBERS, label: 'Members' },
  { value: OtherItemVisibility.ADMINS, label: 'Admins' },
];

const getVisibilityLabel = (visibility?: OtherItemVisibility | null) =>
  OTHER_ITEM_VISIBILITY_OPTIONS.find((option) => option.value === visibility)
    ?.label ?? 'Public';

// this one is for form values
type CreateOtherItemForm = {
  name: string;
  categoryId: string;
  units?: number | null;
  isLimitedUnits: boolean;
  price: number | null; // simple number for input
  description?: string | null;
  chartOfAccountId?: string;
  accountClassId?: string;
  visibility: OtherItemVisibility;
};

type CreateOtherItemModalProps = {
  isOpen: boolean;
  onClose: () => void;
  // item?: GetOtherItems_getOtherItems_results | null;
  item?: any | null;
  mode: 'create' | 'edit' | 'view';
};

// ---------------- VIEW FIELD ----------------

const ViewField = ({
  label,
  value,
}: {
  label: string;
  value?: React.ReactNode;
}) => (
  <FormControl>
    <FormLabel fontWeight="bold">{label}</FormLabel>
    <Text px={3} py={2} bg="gray.50" borderRadius="md">
      {value ?? '-'}
    </Text>
  </FormControl>
);

export const CreateOtherItemModal: React.FC<CreateOtherItemModalProps> = ({
  isOpen,
  onClose,
  item,
  mode,
}) => {
  const current = useCurrent();
  const toast = useToast();
  const { currency } = useDefaultCurrency();
  const isEditMode = !!item;
  const isViewMode = mode === 'view';
  const { createOtherItem, loading: creating } = useCreateOtherItem();
  const { updateOtherItem, loading: updating } = useUpdateOtherItem();
  // states for search inputs in the dropdowns
  const [search, setSearch] = React.useState('');
  const [accountClassSearch, setAccountClassSearch] = React.useState('');
  const [chartOfAccountSearch, setChartSearch] = React.useState('');

  const {
    register,
    handleSubmit,
    reset,
    watch,
    // control,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<CreateOtherItemForm>({
    defaultValues: {
      name: '',
      categoryId: '',
      units: 0,
      isLimitedUnits: false,
      price: 0,
      description: '',
      chartOfAccountId: '',
      accountClassId: '',
      visibility: OtherItemVisibility.PUBLIC,
    },
  });

  const isLimitedUnits = watch('isLimitedUnits');

  const [chartOfAccounts, setChartOfAccounts] = React.useState<
    ChartOfAccountItem[]
  >([]);
  const [accountClasses, setAccountClasses] = React.useState<
    accountClassItem[]
  >([]);

  const DEFAULT_VALUES = React.useMemo(() => {
    // const price = item && item?.price ? Number(currencyDisplay(dinero(item?.price)).replace('$', '')) || null : null;

    const price = item?.price || null;
    const scale = typeof price?.scale === 'number' ? price.scale : 2;
    const divider = Math.pow(10, scale);
    const mainPrice = (price?.amount || 0) / divider;
    return {
      name: item?.name ?? '',
      categoryId: item?.categoryId ?? '',
      description: item?.description ?? null,
      isLimitedUnits: item?.isLimitedUnits ?? false,
      units: typeof item?.units === 'number' ? item?.units : null,
      price: mainPrice,
      visibility: item?.visibility ?? OtherItemVisibility.PUBLIC,
      // chartOfAccountId: item?.chartOfAccountId ?? "",
      // accountClassId: item?.accountClassId ?? "",
    };
  }, [item]);

  const { data: categoryData, loading: categoryLoading } = useQuery(
    GET_CATEGORY,
    {
      variables: {
        input: {
          search: search,
          limit: 100, // enough for dropdown
          offset: 0,
          filter: {
            status: OtherItemCategoryStatus.ACTIVE,
          },
        },
      },
      fetchPolicy: 'network-only',
    }
  );

  const categories = React.useMemo(
    () => categoryData?.getCategories?.results ?? [],
    [categoryData]
  );
  // Fetch Chart of Accounts data
  React.useEffect(() => {
    fetchChartOfAccounts({
      tenantId: current.tenant?._id,
      search: chartOfAccountSearch,
      limit: 10,
    })
      .then((res) => setChartOfAccounts(res.data.data || []))
      .catch(() => setChartOfAccounts([]));
  }, [chartOfAccountSearch]);
  // Fetch Account Class data
  React.useEffect(() => {
    fetchAccountClass({
      tenantId: current.tenant?._id,
      search: accountClassSearch,
      limit: 10,
    })
      .then((res) => setAccountClasses(res.data.data || []))
      .catch(() => setAccountClasses([]));
  }, [accountClassSearch]);

  const handleClose = () => {
    reset();
    onClose();
  };

  // when user submit the form, we need to first create/update the other item
  const submitHandler = async (form: CreateOtherItemForm) => {
    try {
      /* ===============================
         STEP 1: CREATE / UPDATE OTHER ITEM (GRAPHQL)
         =============================== */

      const visibility =
        form.visibility || OtherItemVisibility.PUBLIC;

      const payload: CreateOtherItemInput = {
        name: form.name,
        categoryId: form.categoryId,
        isLimitedUnits: form.isLimitedUnits,
        units: form.isLimitedUnits ? Number(form.units || 0) : 0,
        description: form.description || null,
        price: {
          amount: Number(form.price),
          currency,
          scale: 2,
        },
        status: isEditMode
          ? item?.status ?? OtherItemStatus.ACTIVE
          : OtherItemStatus.ACTIVE,
        visibility,
      };

      const gqlResponse = isEditMode
        ? await updateOtherItem({
            _id: item!._id,
            ...payload,
            visibility,
          })
        : await createOtherItem(payload);

      if (!gqlResponse?.success) {
        throw new Error(gqlResponse?.message);
      }
      const shouldCallFinancialApi =
        Boolean(form.chartOfAccountId) || Boolean(form.accountClassId);

      /* ===============================
         STEP 2: GET OTHER ITEM ID
         =============================== */
      const otherItemId = isEditMode
        ? item!._id ?? gqlResponse.otherItem?._id
        : gqlResponse.otherItem?._id;

      if (!otherItemId) {
        throw new Error('Other item ID not found');
      }

      /* ===============================
         STEP 3: CALL REST API (FINANCIAL ASSOCIATION)
         =============================== */

      if (shouldCallFinancialApi) {
        const associationPayload = {
          tenantId: current.tenant?._id!,
          entityId: otherItemId,
          entityType: FinancialAssociationEntityType.OTHERITEM,
          chartOfAccountId: form.chartOfAccountId || null,
          accountClassId: form.accountClassId || null,
          createdByUserId: current.user?._id!,
          updatedByUserId: current.user?._id!,
        };

        isEditMode
          ? await updateFinancialAssociation(associationPayload)
          : await createFinancialAssociation(associationPayload);
      }

      /* ===============================
         STEP 4: SUCCESS
         =============================== */

      toast({
        title: isEditMode ? 'Updated' : 'Created',
        description: 'Item and financial association saved successfully',
        status: 'success',
      });

      onClose();
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err.message || 'Something went wrong',
        status: 'error',
      });
    }
  };
  // get financial association while update and view time
  const shouldFetchFinancialAssociation =
    !!current.tenant?._id && !!item?._id && (isEditMode || isViewMode);

  const fetchFinancialAssociation = async () => {
    if (!current.tenant?._id || !item?._id) return;

    try {
      const response = await getFinancialAssociation({
        tenantId: current.tenant._id,
        entityId: item._id,
      });

      if (!response.status || !response.data) return;

      const { chartOfAccountId, accountClassId } = response.data;

      /* ===============================
         SET FORM VALUES (EDIT MODE)
         =============================== */

      setValue('chartOfAccountId', chartOfAccountId, {
        shouldDirty: false,
        shouldValidate: true,
      });

      setValue('accountClassId', accountClassId, {
        shouldDirty: false,
        shouldValidate: true,
      });
    } catch (error) {
      console.error('Failed to fetch financial association', error);
    }
  };

  React.useEffect(() => {
    if (!shouldFetchFinancialAssociation) return;
    fetchFinancialAssociation();
  }, [
    shouldFetchFinancialAssociation,
    item?._id,
    chartOfAccounts.length,
    accountClasses.length,
  ]);

  React.useEffect(() => {
    reset(DEFAULT_VALUES);
  }, [DEFAULT_VALUES, reset]);

  // selected names for view mode
  const selectedCategoryName = categories.find(
    (c: any) => c._id === item?.categoryId
  )?.name;
  const selectedChartOfAccountName =
    chartOfAccounts.find((i: any) => i.id === watch('chartOfAccountId'))
      ?.description || '-';

  const selectedAccountClassName =
    accountClasses.find((i: any) => i.id === watch('accountClassId'))
      ?.description || '-';

  // this is to register categoryId, visibility, chartOfAccountId, and accountClassId, since we are using CustomSelect
  React.useEffect(() => {
    register('categoryId', {
      required: 'Category is required',
    });
    register('visibility', {
      required: 'Visibility is required',
    });
    register('chartOfAccountId');
    register('accountClassId');
  }, [register]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={!isSubmitting ? handleClose : () => {}}
      size="xl"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {isViewMode
            ? 'View Other Item'
            : isEditMode
            ? 'Update Other Item'
            : 'Create Other Item'}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={5}>
            <HStack spacing={6} w="100%">
              {/* Item Name */}
              {isViewMode ? (
                <ViewField label="Item Name" value={item?.name} />
              ) : (
                <FormControl isInvalid={!!errors.name} isRequired>
                  <FormLabel>Item Name</FormLabel>
                  <Input
                    placeholder="Enter item name"
                    {...register('name', {
                      required: 'Item name is required',
                      maxLength: {
                        value: 150,
                        message: 'Item name cannot exceed 150 characters',
                      },
                    })}
                  />
                  <FormErrorMessage>{errors.name?.message}</FormErrorMessage>
                </FormControl>
              )}

              {/* Category */}
              {isViewMode ? (
                <ViewField label="Category" value={selectedCategoryName} />
              ) : (
                <FormControl isInvalid={!!errors.categoryId} isRequired>
                  <CustomSelect
                    label="Category"
                    isRequired
                    placeholder="Select Category"
                    value={watch('categoryId')}
                    options={categories.map(
                      (cat: { name: string; _id: string }) => ({
                        label: cat.name,
                        value: cat._id,
                      })
                    )}
                    isLoading={categoryLoading}
                    /* Backend search */
                    onSearch={(text) => {
                      setSearch(text); // your state
                    }}
                    /* Update form value manually */
                    onChange={(value) => {
                      setValue('categoryId', value, {
                        shouldValidate: true,
                        shouldDirty: true,
                      });
                    }}
                    /* Show validation error */
                    error={errors.categoryId?.message}
                  />
                </FormControl>
              )}
            </HStack>
            <HStack spacing={6} w="100%" align="flex-start">
              {/* Visibility */}
              {isViewMode ? (
                <ViewField
                  label="Visibility"
                  value={getVisibilityLabel(item?.visibility)}
                />
              ) : (
                <FormControl isInvalid={!!errors.visibility} isRequired>
                  <CustomSelect
                    label="Visibility"
                    isRequired
                    isSearchable={false}
                    placeholder="Select Visibility"
                    value={watch('visibility')}
                    options={OTHER_ITEM_VISIBILITY_OPTIONS}
                    onChange={(value) => {
                      setValue('visibility', value as OtherItemVisibility, {
                        shouldValidate: true,
                        shouldDirty: true,
                      });
                    }}
                    error={errors.visibility?.message}
                  />
                </FormControl>
              )}

              {/* Unlimited Units */}
              {isViewMode ? (
                <ViewField
                  label="Limited Units"
                  value={item?.isLimitedUnits ? 'Yes' : 'No'}
                />
              ) : (
                <FormControl pt={8}>
                  <Checkbox {...register('isLimitedUnits')} w="100%">
                    Limited Units
                  </Checkbox>
                </FormControl>
              )}
            </HStack>
            <HStack spacing={6} w="100%">
              {/* Units */}
              {isViewMode ? (
                <>
                  {item?.units != null && (
                    <ViewField
                      label="Unit"
                      value={item?.isLimitedUnits ? item?.units : 'No Limit'}
                    />
                  )}
                </>
              ) : (
                <>
                  {isLimitedUnits && (
                    <FormControl isInvalid={!!errors.units}>
                      <FormLabel>Unit</FormLabel>
                      <Input
                        type="number"
                        placeholder="Enter unit"
                        {...register('units', {
                          required: isLimitedUnits ? 'Unit is required' : false,
                          min: {
                            value: 1,
                            message: 'Units must be at least 1',
                          },
                          validate: (v) => {
                            if (!isLimitedUnits) return true;
                            if (!Number.isInteger(Number(v))) {
                              return 'Units must be a whole number';
                            }
                            return true;
                          },
                          setValueAs: (v) => (v === '' ? null : Number(v)),
                        })}
                      />
                      <FormErrorMessage>
                        {errors.units?.message}
                      </FormErrorMessage>
                    </FormControl>
                  )}
                </>
              )}

              {/* Price */}
              {isViewMode ? (
                <ViewField
                  label="Price"
                  value={`${currencyDisplay(dinero(item?.price)) ?? 0}`}
                />
              ) : (
                <FormControl isInvalid={!!errors.price}>
                  <FormLabel>Price</FormLabel>
                  <Input
                    type="number"
                    placeholder="Enter price"
                    {...register('price', {
                      required: 'Price is required',
                      min: {
                        value: 0.01,
                        message: 'Price must be greater than 0',
                      },
                      validate: (v) => {
                        if (Number(v) <= 0) return 'Invalid price';
                        return true;
                      },
                      setValueAs: (v) => (v === '' ? '' : Number(v)),
                    })}
                  />
                  <FormErrorMessage>{errors.price?.message}</FormErrorMessage>
                </FormControl>
              )}
            </HStack>

            <HStack spacing={6} w="100%">
              {/* Chart of Account */}
              {isViewMode ? (
                <ViewField
                  label="Chart of Account"
                  value={selectedChartOfAccountName}
                />
              ) : (
                <FormControl isInvalid={!!errors.chartOfAccountId}>
                  <CustomSelect
                    label="Chart of Account"
                    placeholder="Select Chart of Account"
                    value={watch('chartOfAccountId') || ''}
                    options={chartOfAccounts
                      .filter((i) => i.status === 'active')
                      .map((item) => ({
                        label: item.description,
                        value: item.id,
                      }))}
                    isLoading={false}
                    onSearch={(text) => {
                      setChartSearch(text);
                    }}
                    onChange={(value) => {
                      setValue('chartOfAccountId', value, {
                        shouldValidate: true,
                        shouldDirty: true,
                      });
                    }}
                    error={errors.chartOfAccountId?.message}
                  />
                </FormControl>
              )}

              {/* Account Class */}
              {isViewMode ? (
                <ViewField
                  label="Account Class"
                  value={selectedAccountClassName}
                />
              ) : (
                <FormControl isInvalid={!!errors.accountClassId}>
                  <CustomSelect
                    label="Account Class"
                    placeholder="Select Account Class"
                    value={watch('accountClassId') || ''}
                    options={accountClasses
                      .filter((i) => i.status === 'active')
                      .map((item) => ({
                        label: item.description,
                        value: item.id,
                      }))}
                    isLoading={false}
                    onSearch={(text) => {
                      setAccountClassSearch(text);
                    }}
                    onChange={(value) => {
                      setValue('accountClassId', value, {
                        shouldValidate: true,
                        shouldDirty: true,
                      });
                    }}
                    error={errors.accountClassId?.message}
                  />
                </FormControl>
              )}

              {/* <FormControl isInvalid={!!errors.chartOfAccountId}>
                <FormLabel>Chart of Account</FormLabel>
                <Controller
                  name="chartOfAccountId"
                  control={control}
                  rules={{ required: "Chart of account is required" }}
                  render={({ field }) => (
                    <Select {...field} placeholder="-- Select --">
                      {chartOfAccounts
                        .filter((i) => i.status === "active")
                        .map((item) => (
                          <option key={item.id} value={item.id}>
                            {item.code} - {item.description}
                          </option>
                        ))}
                    </Select>
                  )}
                />
                <FormErrorMessage>
                  {errors.chartOfAccountId?.message}
                </FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.accountClassId}>
                <FormLabel>Account Class</FormLabel>
                <Controller
                  name="accountClassId"
                  control={control}
                  rules={{ required: "Account class is required" }}
                  render={({ field }) => (
                    <Select {...field} placeholder="-- Select --">
                      {accountClasses
                        .filter((i) => i.status === "active")
                        .map((item) => (
                          <option key={item.id} value={item.id}>
                            {item.code} - {item.description}
                          </option>
                        ))}
                    </Select>
                  )}
                />
                <FormErrorMessage>
                  {errors.accountClassId?.message}
                </FormErrorMessage>
              </FormControl> */}
            </HStack>

            {/* Description */}
            {isViewMode ? (
              <ViewField label="Description" value={item?.description || '-'} />
            ) : (
              <FormControl>
                <FormLabel>Description</FormLabel>
                <Textarea
                  placeholder="Enter item description"
                  {...register('description')}
                />
              </FormControl>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter gap={3}>
          <Button variant="outline" onClick={handleClose}>
            {isViewMode ? 'Close' : 'Cancel'}
          </Button>

          {!isViewMode && (
            <Button
              colorScheme="blue"
              isLoading={creating || updating || isSubmitting}
              onClick={handleSubmit(submitHandler)}
            >
              {isEditMode ? 'Update' : 'Create'}
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default CreateOtherItemModal;
